import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import TM from "@/components/TM";

// 22 static stars — upper 60% of screen. No animation.
const LANDING_STARS = [
  { left: "8%",  top: "5%",  size: 1.5, opacity: 0.50 },
  { left: "15%", top: "12%", size: 1,   opacity: 0.40 },
  { left: "23%", top: "8%",  size: 1,   opacity: 0.60 },
  { left: "32%", top: "3%",  size: 1.5, opacity: 0.35 },
  { left: "41%", top: "15%", size: 1,   opacity: 0.50 },
  { left: "53%", top: "7%",  size: 1.5, opacity: 0.45 },
  { left: "62%", top: "18%", size: 1,   opacity: 0.55 },
  { left: "71%", top: "5%",  size: 1.5, opacity: 0.40 },
  { left: "78%", top: "22%", size: 1,   opacity: 0.60 },
  { left: "88%", top: "9%",  size: 1,   opacity: 0.35 },
  { left: "94%", top: "15%", size: 1.5, opacity: 0.50 },
  { left: "3%",  top: "28%", size: 1,   opacity: 0.45 },
  { left: "18%", top: "35%", size: 1,   opacity: 0.30 },
  { left: "29%", top: "42%", size: 1.5, opacity: 0.55 },
  { left: "45%", top: "38%", size: 1,   opacity: 0.40 },
  { left: "57%", top: "45%", size: 1,   opacity: 0.35 },
  { left: "68%", top: "33%", size: 1.5, opacity: 0.50 },
  { left: "82%", top: "40%", size: 1,   opacity: 0.45 },
  { left: "91%", top: "52%", size: 1,   opacity: 0.30 },
  { left: "11%", top: "50%", size: 1.5, opacity: 0.40 },
  { left: "37%", top: "57%", size: 1,   opacity: 0.35 },
  { left: "73%", top: "55%", size: 1,   opacity: 0.50 },
];

export default function MitraaWelcome() {
  const navigate = useNavigate();

  // If already authenticated, skip the landing page
  useEffect(() => {
    if (sessionStorage.getItem("student_token")) {
      navigate("/home", { replace: true });
    } else if (sessionStorage.getItem("teacher_token")) {
      navigate("/teacher", { replace: true });
    }
  }, [navigate]);

  return (
    <div
      className="relative min-h-screen flex flex-col items-center justify-center px-4 overflow-hidden"
      style={{ background: "linear-gradient(to bottom, #0d1b2a 0%, #1a3a5c 55%, #2e4a6b 100%)" }}
      data-testid="mitraa-welcome"
    >
      {/* Static stars */}
      {LANDING_STARS.map((s, i) => (
        <div
          key={i}
          aria-hidden="true"
          style={{
            position: "absolute",
            left: s.left,
            top: s.top,
            width: s.size,
            height: s.size,
            borderRadius: "50%",
            background: "#ffffff",
            opacity: s.opacity,
            pointerEvents: "none",
          }}
        />
      ))}

      <div className="w-full max-w-sm flex flex-col items-center text-center relative z-10">

        {/* Logo mark */}
        <div className="mb-8">
          <img
            src="/mitraa-logo-transparent.webp"
            alt="Mitraa"
            className="w-28 h-28 object-contain"
            style={{ filter: "brightness(0) invert(1) drop-shadow(0 0 22px rgba(96,165,250,0.75))" }}
          />
        </div>

        {/* Heading */}
        <h1
          className="font-display text-4xl sm:text-5xl font-bold text-white leading-tight mb-3"
          data-testid="welcome-heading"
        >
          Welcome to Mitraa<TM />
        </h1>

        {/* Tagline */}
        <p className="text-lg mb-12" style={{ color: "rgba(176, 219, 255, 0.92)", letterSpacing: "0.05em" }} data-testid="welcome-tagline">
          A true companion in learning
        </p>

        {/* Role buttons */}
        <div className="w-full flex flex-col gap-4">
          <Button
            onClick={() => navigate("/login")}
            className="w-full py-4 rounded-2xl text-base font-semibold bg-sky-500 hover:bg-sky-600 text-white shadow-md hover:shadow-lg transition-all"
            data-testid="student-entry-btn"
          >
            <BookOpen className="w-5 h-5 mr-2.5" />
            I'm a Student
          </Button>

          <Button
            onClick={() => navigate("/teacher/login")}
            variant="outline"
            className="w-full py-4 rounded-2xl text-base font-semibold border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all"
            data-testid="teacher-entry-btn"
          >
            <GraduationCap className="w-5 h-5 mr-2.5" />
            I'm a Teacher
          </Button>
        </div>

        {/* Gateway back-link */}
        <a
          href="https://adiutthan.com"
          className="mt-6 inline-flex items-center justify-center gap-2 text-sm font-medium transition-colors hover:opacity-90"
          style={{ color: "rgba(176, 219, 255, 0.85)" }}
          data-testid="gateway-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to AdiUtthan Home Page
        </a>
      </div>

      {/* Footer */}
      <p className="absolute bottom-6 text-xs z-10" style={{ color: "rgba(255,255,255,0.60)" }} data-testid="welcome-footer">
        A product of AdiUtthan, powered by Adiora<TM /> Engine
      </p>
    </div>
  );
}
